import React, { useRef } from "react";

const sections = [
  {
    id: "Investor",
    title: "✅ Effectively Attract More Investors (Dell, ADM)",
    content: [
      {
        subtitle: "Ethical Business Practices and Financial Performance",
        text: "Research confirms that companies with strong ethical practices experience improved financial outcomes. Ethical companies tend to have higher market value, attract institutional investors, and benefit from lower costs of capital. Investors increasingly prioritize corporate social responsibility (CSR) and ethical governance, seeing them as long-term financial opportunities rather than just moral choices. Firms included in ethical certification programs, such as the Ethisphere list, have seen an increase in investor confidence and reduced investment risks【10】.",
      },
      {
        subtitle: "💡 Supporting Case Study: Dell",
        text: `Dell’s long-standing commitment to ethical governance has positioned it as a leader in ethical business practices. Its zero-tolerance corruption policy, AI-powered risk detection system, and supplier compliance framework have earned it recognition from Ethisphere for 12 consecutive years. This has enhanced investor confidence, leading to $1.1 billion in shareholder returns and securing 99% of Fortune 500 companies as clients. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/Dell.pdf', '_blank')">[Learn more]</span>`,
      },
      {
        subtitle: "💡 Supporting Case Study: ADM",
        text: `ADM, a global agricultural firm, has leveraged its strong ethics and transparency to issue $7.4 billion in sustainable bonds, attracting ESG-focused investors. Ethical commitments have also helped ADM avoid legal fines and secure strategic partnerships. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/ADM.pdf', '_blank')">[Learn more]</span>`,
      },
    ],
  },
  {
    id: "Consumer",
    title: "✅ Enhance consumer trust (L’Oréal, Best Buy)",
    content: [
      {
        subtitle: "Brand Reputation and Customer Loyalty",
        text: "Ethical branding and corporate social responsibility build strong consumer trust, leading to greater brand loyalty and a willingness to pay premium prices. Customers are more likely to support companies that prioritize sustainability, fair pricing, and transparency. Ethical brands such as Patagonia and The Body Shop have successfully leveraged their ethical commitments to enhance customer retention and loyalty【12】. Conversely, unethical marketing, such as false advertising or misleading claims, damages brand credibility and can lead to consumer backlash.",
      },
      {
        subtitle: "💡 Supporting Case Study: L’Oréal",
        text: `L’Oréal’s ethical sourcing and sustainability efforts have led to an 86% consumer trust rating, with sustainability-linked products growing 18% in sales. Since its recognition by Ethisphere, L’Oréal has expanded into three new global markets, reinforcing how ethics drive competitive expansion. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/Oreal.pdf', '_blank')">[Learn more]</span>`,
      },
      {
        subtitle: "💡 Supporting Case Study: Best Buy",
        text: `Best Buy’s supplier compliance audits and anti-corruption initiatives have improved consumer trust, contributing to a 14% employee turnover rate (significantly lower than the industry average of 30%) and $4.6 billion in customer savings through sustainable product initiatives. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/BestBuy.pdf', '_blank')">[Learn more]</span>`,
      },
    ],
  },
  {
    id: "Marketing",
    title: "✅  Achieve competitive market positioning (JLL, Capgemini)",
    content: [
      {
        subtitle: "Competitive Advantage and Market Positioning",
        text: "Business ethics provide a competitive edge by fostering long-term sustainability and reducing reputational risks. Companies that integrate ethics into their business models experience better employee retention, stakeholder trust, and overall business growth. Studies indicate that businesses operating in global markets need strong ethical frameworks to maintain their &quot;license to operate&quot; and avoid regulatory penalties【14】",
      },
      {
        subtitle: "💡 Supporting Case Study: JLL",
        text: `JLL’s "Ethics Everywhere" program has helped it retain 96 out of the Fortune 100 companies as clients, proving that ethical leadership fosters long-term business relationships. Ethical governance has also driven its sustainability services revenue growth <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/JLL.pdf', '_blank')">[Learn more]</span>`,
      },
      {
        subtitle: "💡 Supporting Case Study: Capgemini",
        text: `Capgemini’s adherence to ISO 37001 anti-bribery standards and transparent corporate governance have fueled a €22.52 billion revenue growth (+2.4%), along with €1.44 billion returned to shareholders due to increased investor confidence. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/Campgemini.pdf', '_blank')">[Learn more]</span>`,
      },
    ],
  },
  {
    id: "Risk",
    title: "✅  Mitigate legal and financial risks (U.S. Bancorp, Allstate)",
    content: [
      {
        subtitle: "Risk Management and Legal Compliance",
        text: "Ethical business practices serve as a proactive risk management strategy, protecting companies from financial losses due to fraud, regulatory violations, and reputational damage. Companies that engage in CSR initiatives, particularly in controversial industries like tobacco and alcohol, benefit from an &quot;insurance-like&quot; effect that mitigates financial volatility【13】. Strong ethical policies also prevent crises, as seen in the failures of companies like Enron and Volkswagen, which suffered due to unethical decision-making.",
      },
      {
        subtitle: "💡 Supporting Case Study: U.S. Bancorp",
        text: `By implementing AI-powered fraud detection, U.S. Bancorp prevented $5.2 billion in fraudulent transactions and avoided major financial crime-related lawsuits. Its ethics and compliance measures have also led to a 12% increase in customer satisfaction. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/UsBank.pdf', '_blank')">[Learn more]</span>`,
      },
      {
        subtitle: "💡 Supporting Case Study: Allstate",
        text: `Allstate’s zero-tolerance corruption policy has minimized compliance risks, contributing to 11.1% revenue growth ($57.1 billion total revenue). By enforcing strict anti-fraud controls, Allstate has avoided regulatory fines and reinforced consumer trust. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/Allstate.pdf', '_blank')">[Learn more]</span>`,
      },
    ],
  },
  {
    id: "Employee",
    title: "✅  Retain employees and build workplace culture (Voya, EDP)",
    content: [
      {
        subtitle: "Employee Satisfaction and Retention",
        text: "Employees prefer to work for ethical organizations, leading to lower turnover rates and higher engagement. Ethical leadership fosters a positive workplace culture, enhancing employee motivation and productivity. Studies show that Millennials and Gen Z workers prioritize corporate values and CSR initiatives when selecting employers. Companies that fail to uphold ethical standards risk losing top talent, while businesses that integrate ethical hiring and leadership strategies build stronger, more loyal teams【15】.",
      },
      {
        subtitle: "💡 Supporting Case Study: Voya Financial",
        text: `Voya’s ethical commitments have driven an employee Net Promoter Score (NPS) of 75+, and its ethical business culture has contributed to 96% customer satisfaction. It has also attracted long-term investors due to its commitment to ESG principles. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/Voya.pdf', '_blank')">[Learn more]</span>`,
      },
      {
        subtitle: "💡 Supporting Case Study: EDP",
        text: `EDP’s ethical leadership has resulted in 80%+ employee confidence in the company’s ethics policies and a 5% rise in customer satisfaction. Ethics-driven governance also secured €6 billion in sustainable financing, reinforcing long-term stability. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/EDP.pdf', '_blank')">[Learn more]</span>`,
      },
    ],
  },
  {
    id: "Sustainability",
    title: "✅  Ensure long-term business sustainability (ADM, L’Oréal)",
    content: [
      {
        subtitle: "Long-Term Business Sustainability",
        text: "Ethical business strategies are not just about compliance—they contribute to long-term business success. Ethical companies outperform competitors by maintaining strong relationships with customers, investors, and employees. Self-regulation beyond legal requirements enhances corporate credibility and ensures sustainability. Companies that go beyond mere compliance and embed ethics into their core operations enjoy long-term profitability and brand strength【13】【14】.",
      },
      {
        subtitle: "💡 Supporting Case Study: ADM",
        text: `ADM’s sustainability initiatives have positioned it as a preferred supplier for government contracts and multinational companies. Its investment-grade credit rating and ability to issue sustainable bonds have strengthened financial resilience. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/ADM.pdf', '_blank')">[Learn more]</span>`,
      },
      {
        subtitle: "💡 Supporting Case Study: L’Oréal",
        text: `By committing to long-term sustainability, L’Oréal has seen 41.18 billion euros in revenue, 20% more investment from sustainability-focused funds, and an annual shareholder return of 19%, significantly outperforming its competitors. <span class='text-blue-500 hover:text-blue-700 cursor-pointer' onclick="window.open('/pdfs/Oreal.pdf', '_blank')">[Learn more]</span>`,
      },
    ],
  },
];

export default function EthicalBusinessSection() {
  const sectionRefs = useRef({});

  const scrollToSection = (id) => {
    const targetSection = sectionRefs.current[id];
    if (targetSection) {
      const offsetTop = targetSection.offsetTop;
      window.scrollTo({
        top: offsetTop - 44, // Apply the -16 margin offset
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" id="research-evidence">
      <h2 className="text-center text-2xl font-bold mb-12 mt-12">
        Data-Driven Benefits of Supporting Our Cause
      </h2>

      {/* Index Section - Moves to top on mobile */}
      <div className="flex flex-col md:flex-row">
        {/* Index Column */}
        <div className="w-full md:w-1/3 md:pr-4 md:border-r border-gray-300 mb-4 md:mb-0 md:ml-2 bg-[#FFFFFF] rounded">
          <ul className="space-y-2 md:space-y-4 flex flex-wrap md:flex-col">
            {sections.map((section) => (
              <li key={section.id} className="flex-1">
                <button
                  className="block w-full text-left px-2 py-1 rounded hover:bg-gray-200 whitespace-nowrap m-2"
                  onClick={() => scrollToSection(section.id)}
                >
                  <span>
                    {section.title.split("(")[0]}
                    {/* Text before parentheses */}
                    <span className="text-gray-400">
                      {" "}
                      <br />({section.title.split("(")[1]}{" "}
                      {/* Text inside parentheses */}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Content Column */}
        <div className="w-full md:w-2/3 md:pl-8">
          {sections.map((section) => (
            <div
              key={section.id}
              ref={(el) => (sectionRefs.current[section.id] = el)}
              className="mb-16"
            >
              <h3 className="text-md font-base mb-2">{section.title}</h3>
              {section.content.map((para, index) => (
                <div key={index} className="mb-4">
                  <h4
                    className={`font-medium ${
                      index === 0 ? "text-2xl text-[#004E98]" : "text-lg"
                    }`}
                  >
                    {para.subtitle}
                  </h4>
                  <p
                    className="text-gray-600 font-light"
                    dangerouslySetInnerHTML={{ __html: para.text }}
                  ></p>
                </div>
              ))}
            </div>
          ))}

          {/* Conclusion Section */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-[#004E98] mb-4">
              Conclusion
            </h3>
            <p className="text-gray-600 font-light mb-4">
              Business ethics is not a trade-off against profitability; rather,
              it is a strategic advantage. Ethical companies attract investors,
              build customer trust, reduce financial risks, retain top talent,
              and ensure long-term sustainability. As consumer and investor
              expectations evolve, businesses that prioritize ethical practices
              will continue to outperform competitors and drive industry
              leadership.
            </p>

            <hr className="border-t-1 border-gray-300 my-8" />

            <h4 className="text-xl font-semibold text-gray-600 mb-2 mt-12">
              Reference Numbers for More Insight
            </h4>
            <ul className="list-disc pl-5 text-gray-500">
              <li>
                <strong>【10】:</strong> Investor-related insights – Discusses
                how ethics influence investor behavior, company valuation, and
                cost of capital.{" "}
                <a
                  href="/pdfs/Investor.pdf"
                  className="text-blue-500 hover:text-blue-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  [See Insight]
                </a>
              </li>
              <li>
                <strong>【12】:</strong> Customer-side insights – Highlights how
                ethical business practices improve brand loyalty and customer
                trust.{" "}
                <a
                  href="/pdfs/Customer.pdf"
                  className="text-blue-500 hover:text-blue-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  [See Insight]
                </a>
              </li>
              <li>
                <strong>【13】:</strong> Risk management insights – Explains how
                ethics help mitigate business risks and prevent financial
                instability.{" "}
                <a
                  href="/pdfs/Risk.pdf"
                  className="text-blue-500 hover:text-blue-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  [See Insight]
                </a>
              </li>
              <li>
                <strong>【14】:</strong> Competitive advantage insights – Shows
                how ethical business strategies contribute to market positioning
                and long-term success.{" "}
                <a
                  href="/pdfs/Competitive.pdf"
                  className="text-blue-500 hover:text-blue-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  [See Insight]
                </a>
              </li>
              <li>
                <strong>【15】:</strong> Employee-related insights – Examines
                the role of ethics in employee retention, motivation, and
                workplace culture.{" "}
                <a
                  href="/pdfs/Emplyee.pdf"
                  className="text-blue-500 hover:text-blue-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  [See Insight]
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
